import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import ItemList from 'Components/Product/Seo/ItemList';
import useViewport from 'Hooks/useViewport';
import SiteSlider from 'Components/Utility/SiteSlider/SiteSlider';
import HitCard from 'Components/Cards/HitCard';

export default function MoreThanJustGames({
  fantasyVerseHits,
  booksStandardHits,
  softwareHits,
  elearningHits,
  audioHits,
}) {
  const [tab, setTab] = useState('fantasyverse');

  const { isAboveMd } = useViewport();

  let nonGameProducts = fantasyVerseHits;
  let viewAllLink = '/fantasyverse';

  if (tab === 'fantasyverse') {
    nonGameProducts = fantasyVerseHits;
    viewAllLink = '/fantasyverse';
  }
  if (tab === 'books') {
    nonGameProducts = booksStandardHits;
    viewAllLink = '/search?types=book%2Cbook-bundle%2Ccomic%2Cgraphic-novel%2Ccomic-bundle';
  }
  if (tab === 'software') {
    nonGameProducts = softwareHits;
    viewAllLink = '/search?types=software%2Csoftware-bundle';
  }
  if (tab === 'elearning') {
    nonGameProducts = elearningHits;
    viewAllLink = '/search?types=elearning%2Celearning-bundle';
  }
  if (tab === 'audio') {
    nonGameProducts = audioHits;
    viewAllLink = '/search?types=audio%2Caudio-bundle';
  }

  const sliderSettings = {
    xs: {
      slidesToShow: 1,
      slidesToScroll: 1,
    },
    sm: {
      slidesToShow: 2,
      slidesToScroll: 2,
    },
    md: {
      slidesToShow: 2,
      slidesToScroll: 2,
    },
    lg: {
      slidesToShow: 4,
      slidesToScroll: 4,
    },
    xl: {
      slidesToShow: 4,
      slidesToScroll: 4,
    },
    xxl: {
      slidesToShow: tab === 'fantasyverse' ? 5 : 4,
      slidesToScroll: tab === 'fantasyverse' ? 5 : 4,
    },
  };

  const switchTab = (newTab) => {
    setTab(newTab);
  };

  return (
    <section className="HomePage__nonGames">
      <section className="container ProductTabsSection">
        <div className="ProductTabsSection__titleView">
          <h2>
            <FormattedMessage
              id="more-than-just-games.title"
              defaultMessage="More Than Just Games"
            />
          </h2>
          {!isAboveMd &&
            <Link to={viewAllLink} className="ProductTabsSection__viewLink">
              <FormattedMessage
                id="more-than-just-games.link.view-all"
                defaultMessage="View all"
              />
            </Link>
          }
        </div>

        <div className="ProductTabsSection__tabsView">
          <div className="ProductTabsSection__tabs">
            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'fantasyverse' })} onClick={() => { switchTab('fantasyverse'); }}>
              FantasyVerse
            </button>
            <ItemList hits={fantasyVerseHits} title="FantasyVerse" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'books' })} onClick={() => { switchTab('books'); }}>
              <FormattedMessage id="more-than-just-games.tab.books" defaultMessage="Books" />
            </button>
            <ItemList hits={booksStandardHits} title="Books" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'software' })} onClick={() => { switchTab('software'); }}>
              <FormattedMessage id="more-than-just-games.tab.software" defaultMessage="Software" />
            </button>
            <ItemList hits={softwareHits} title="Software" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'elearning' })} onClick={() => { switchTab('elearning'); }}>
              <FormattedMessage id="more-than-just-games.tab.elearning" defaultMessage="eLearning" />
            </button>
            <ItemList hits={elearningHits} title="eLearning" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': tab === 'audio' })} onClick={() => { switchTab('audio'); }}>
              <FormattedMessage id="more-than-just-games.tab.audio" defaultMessage="Audio" />
            </button>
            <ItemList hits={audioHits} title="Audio" />
          </div>

          {isAboveMd &&
            <Link to={viewAllLink} className="ProductTabsSection__viewLink">
              <FormattedMessage
                id="more-than-just-games.link.view-all"
                defaultMessage="View all"
              />
            </Link>
          }
        </div>

        {isEmpty(nonGameProducts) &&
          <SiteSlider sliderClass="ProductCardCarousel" sliderSettings={sliderSettings}>
            {[...Array(15).keys()].map(fakeHit =>
              <div key={fakeHit + 1} className="FakeHitCard">
                <div className={classNames('responsive-image', { 'responsive-image--16by9': tab !== 'fantasyverse', 'responsive-image--2by3': tab === 'fantasyverse' })} />
                <div className="FakeHitCard__strip">
                  <div className="card-icons-price-container" />
                </div>
              </div>,
            )}
          </SiteSlider>
        }

        {!isEmpty(nonGameProducts) &&
          <SiteSlider sliderClass="ProductCardCarousel" sliderSettings={sliderSettings}>
            {nonGameProducts.slice(0, 15).map(hit =>
              <HitCard
                key={hit.product_id}
                xsSize={12}
                smSize={12}
                mdSize={12}
                lgSize={12}
                sizesOverride={'(min-width: 1520px) 307px, (min-width: 1200px) 263px, (min-width: 992px) 298px, (min-width: 768px) 309px, (min-width: 576px) 228px, 95vw'}
                hit={hit}
                adaptiveSize={tab === 'fantasyverse'}
                coverTypeOverride={tab === 'fantasyverse' ? 'portrait' : 'standard'}
              />,
            )}
          </SiteSlider>
        }
      </section>
    </section>
  );
}

MoreThanJustGames.propTypes = {
  fantasyVerseHits: PropTypes.arrayOf(PropTypes.object),
  booksStandardHits: PropTypes.arrayOf(PropTypes.object),
  softwareHits: PropTypes.arrayOf(PropTypes.object),
  elearningHits: PropTypes.arrayOf(PropTypes.object),
  audioHits: PropTypes.arrayOf(PropTypes.object),
};

MoreThanJustGames.defaultProps = {
  fantasyVerseHits: [],
  booksStandardHits: [],
  softwareHits: [],
  elearningHits: [],
  audioHits: [],
};
